import Victor from 'victor';
import { BezierLine } from '@model-framework/link/BezierLine/BezierLine';
import { Point } from '@view-model/models/common/basic/Point';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { SizeType } from '@view-model/models/common/types/ui';

/**
 * buildLinkBezierLine() のアルゴリズムの開始基準座標を返す。
 *
 * 元々は中心座標を開始地点としていたが、ゾーンと付箋のようにサイズが違うもの同士の中心座標を
 * 開始位置としてこのアルゴリズムを適用すると、２つの要素の距離が近い時に開始位置が突き抜ける問題がある。
 * https://github.com/levii/balus-app/issues/1580
 * https://github.com/levii/balus-app/pull/2402
 *
 * このため、付箋のサイズより大きい場合は、相手要素のサイズを使って基準位置を調整する。
 *
 * @param src 基準位置を計算したい図形の矩形領域
 * @param dst リンク相手先となる図形の矩形領域
 * @param nodeSize 付箋のサイズ
 */
function calcBasePoint(src: Rect, dst: Rect, nodeSize: SizeType): Point {
  if (src.width <= nodeSize.width && src.height <= nodeSize.height) {
    return src.getCenterPoint();
  }

  return src
    .applyMarginKeepingCenter(-dst.height / 2, -dst.width / 2, -dst.height / 2, -dst.width / 2, nodeSize)
    .intersectPointFromCenter(dst.getCenterPoint());
}

function buildPoints(
  sourceRect: Rect,
  targetRect: Rect,
  bendingPoint: Victor,
  offset: number,
  nodeSize: SizeType
): [Victor, Victor, Victor] {
  /**
   * ユーザビリティのため、bending Point 上を曲線が通過するようにしたい。
   * 2 次の Bezier 曲線の場合、starting, ending Point の中点と
   * control Point を結んだ線の中点を必ず曲線が通過するので、
   * 逆にこの点を bending Point として control Point を逆算する。
   * ただし、実際には曲線の端点は中心点ではなく、border から始まるため、
   * 後から補正することになる。
   *
   * そのため、
   * sourceView, targetView の中心を結んだ線の中心の 2 分点から
   * bending Point に引いたベクトルの長さを 2 倍にした点を
   * 一旦仮の control Point にする。
   */
  const sourceBasePoint = calcBasePoint(sourceRect, targetRect, nodeSize).toVictor();
  const targetBasePoint = calcBasePoint(targetRect, sourceRect, nodeSize).toVictor();

  let controlPoint = bendingPoint
    .clone()
    .multiply(new Victor(2, 2))
    .subtract(sourceBasePoint.clone().mix(targetBasePoint, 0.5));

  const temporarySourceBezierLine = new BezierLine(
    Point.fromPosition(sourceBasePoint),
    Point.fromPosition(controlPoint),
    Point.fromPosition(targetBasePoint)
  );

  const temporaryTargetBezierLine = new BezierLine(
    Point.fromPosition(targetBasePoint),
    Point.fromPosition(controlPoint),
    Point.fromPosition(sourceBasePoint)
  );

  /**
   * 仮の control Point を利用してベジエ曲線を描画し、その曲線と sourceRect, targetRect の border との交点を
   * 真の starting Point, ending Point とする。
   * ベジエ曲線との交点が取れない場合は、仮の control Point と sourceRect, targetRect の中心を結んだ線と border との交点を
   * 代替の真の starting Point, ending Point とする。
   */
  const startingPoint =
    temporarySourceBezierLine.getIntersectPoint(sourceRect.applyMarginKeepingCenter(offset))?.toVictor() ||
    sourceRect.applyMarginKeepingCenter(offset).intersectPointFromCenter(Point.fromPosition(controlPoint)).toVictor();
  const endingPoint =
    temporaryTargetBezierLine.getIntersectPoint(targetRect.applyMarginKeepingCenter(offset))?.toVictor() ||
    targetRect.applyMarginKeepingCenter(offset).intersectPointFromCenter(Point.fromPosition(controlPoint)).toVictor();

  /**
   * starting, ending Point が決まったので、
   * 改めて先の計算を行い control Point を確定させる。
   */
  controlPoint = bendingPoint.clone().multiply(new Victor(2, 2)).subtract(startingPoint.clone().mix(endingPoint, 0.5));

  return [startingPoint, controlPoint, endingPoint];
}

/**
 * リンクとして描画されるベジエ曲線を返す。
 *
 * 付箋のサイズを引数で受け取るのは、描画を伴わない検査（viewQualityChecks）から、jotai ストアを
 * 参照する StickyNode を経由せずに、描画と同じ曲線を得られるようにするため。
 */
export function buildLinkBezierLine(
  sourceRect: Rect,
  targetRect: Rect,
  bendingPoint: Victor,
  offset: number,
  nodeSize: SizeType
): BezierLine {
  const [startingPoint, controlPoint, endingPoint] = buildPoints(
    sourceRect,
    targetRect,
    bendingPoint,
    offset,
    nodeSize
  ).map((position) => Point.fromPosition(position));

  return new BezierLine(startingPoint, controlPoint, endingPoint);
}
